import { useCallback, useEffect, useState } from "react";
import { AuthenticationGroupDetails, useCoastguard, useUserDetails } from "@cofano/coastguard2-frontend-client";
import { Button, ToolbarItem } from "@cofano/cck";
import { Box, Divider, Popover, Tooltip } from "@mui/material";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import LaunchIcon from "@mui/icons-material/Launch";
import LogoutIcon from "@mui/icons-material/Logout";

import CoastguardSessionSelect from "./CoastguardSessionSelect";
import CoastguardImpersonationIcon from "./CoastguardImpersonationIcon";
import { useStoreState } from "ezStore";
import { intl } from "LocaleWrapper";
import usePopover from "components/hooks/usePopover";
import CoastguardSessionImpersonatorTooltip from "components/layout/coastguard/CoastguardSessionImpersonatorTooltip";
import CoastguardUserDetails from "components/layout/coastguard/CoastguardUserDetails";

type CoastguardProfileToolbarItemProps = {
	onProfileClick?: (() => void) | null;
};

const CoastguardProfileToolbarItem = ({ onProfileClick }: CoastguardProfileToolbarItemProps) => {
	const userDetails = useUserDetails()!;
	const {
		instance: {
			getAuthenticationGroupDetails,
			getAuthorizeApplicationRedirect,
			logout,
			switchActiveSession,
			logoutSession,
			logoutAuthenticationGroupSessions,
		},
	} = useCoastguard();

	const session = useStoreState((state) => state.session);

	const { popoverProps, onPopoverOpen, onPopoverClose } = usePopover({
		trigger: "click",
	});

	const [ authenticationGroupDetails, setAuthenticationGroupDetails ] = useState<AuthenticationGroupDetails | null>(
		null,
	);

	const refetchAuthenticationGroupDetails = useCallback(() => {
		getAuthenticationGroupDetails().then((result) => {
			if (result != null && result.sessions != null && result.sessions.length > 0) {
				setAuthenticationGroupDetails(result);
			} else {
				setAuthenticationGroupDetails(null);
			}
		}).catch(() => setAuthenticationGroupDetails(null));
	}, [ getAuthenticationGroupDetails, setAuthenticationGroupDetails ]);

	const goToProfile = () => {
		if (onProfileClick != null) {
			onProfileClick();
		} else if (session.settings?.settings.coastguardUrl) {
			const coastguardUrl = session.settings.settings.coastguardUrl;
			const path = (coastguardUrl.endsWith("/") ? "" : "/") + "app/profile";
			window.open(coastguardUrl + path, "_blank");
		}
		onPopoverClose();
	};

	const doLogout = async () => {
		await logout();
		onPopoverClose();
	};

	const doSwitchSession = (sessionUuid: string) => {
		// Don't clear session.user up front: switchActiveSession's onSessionSwitched event
		// already replaces it once the new session's details arrive. Clearing it here first
		// left session.user null in the meantime, which PrivateRoute renders as a blank page.
		switchActiveSession(sessionUuid).catch(() => refetchAuthenticationGroupDetails());
		onPopoverClose();
	};

	const doEndSession = (sessionUuid: string) => {
		logoutSession(sessionUuid).finally(() => refetchAuthenticationGroupDetails());
	};

	const goToCoastguard = () => {
		window.open(session.settings?.settings.coastguardUrl, "_blank");
	};

	useEffect(() => {
		if (popoverProps.open) {
			refetchAuthenticationGroupDetails();
		}
	}, [ popoverProps.open, refetchAuthenticationGroupDetails ]);

	const hasMultipleSessions = authenticationGroupDetails != null && authenticationGroupDetails.sessions.length > 1;

	return (
		<>
			<ToolbarItem
				icon={userDetails.impersonator == null ? <AccountCircleIcon /> : <CoastguardImpersonationIcon />}
				label={userDetails.displayName}
				border
				onClick={(event) => {
					event.preventDefault();
					event.stopPropagation();
					onPopoverOpen(event);
				}}
			/>
			<Popover
				{...popoverProps}
				anchorOrigin={{
					vertical: "bottom",
					horizontal: "right",
				}}
				transformOrigin={{
					vertical: -8,
					horizontal: "right",
				}}
				slotProps={{
					paper: {
						sx: {
							maxHeight: "80vh",
							display: "flex",
							flexDirection: "column",
							overflow: "hidden",
						},
					},
				}}
			>
				<Box sx={{ width: 400, display: "flex", flexDirection: "column", minHeight: 0 }}>
					<Box sx={{ p: 2 }}>
						<Box display="flex" gap={2}>
							<CoastguardSessionImpersonatorTooltip impersonator={userDetails.impersonator}>
								<Box
									sx={{
										alignItems: "center",
										display: "flex",
										fontSize: 40,
										lineHeight: 0,
										gridArea: "icon",
									}}
								>
									{userDetails.impersonator == null ? (
										<AccountCircleIcon fontSize="inherit" />
									) : (
										<CoastguardImpersonationIcon
											fontSize="inherit"
											sx={{ color: (theme) => theme.palette.mode === "dark" ? "#C371FF" : "#4B0082" }}
										/>
									)}
								</Box>
							</CoastguardSessionImpersonatorTooltip>

							<CoastguardUserDetails userDetails={userDetails} useSecondaryColors />
						</Box>

						<Box
							sx={{
								mt: 2,
								pt: 1,
								pr: 1,
								pb: 0.5,
								pl: 1,
								border: (theme) => `2px solid ${theme.palette.divider}`,
							}}
						>
							<Box
								sx={{
									display: "flex",
									flexDirection: "column",
									justifyContent: "center",
									position: "relative",
								}}
							>
								<Box
									sx={{
										backgroundColor: (theme) => theme.palette.grey[500],
										color: (theme) => theme.palette.common.white,
										display: "inline-block",
										fontSize: 9,
										fontWeight: 700,
										pl: 0.5,
										pr: 0.5,
										top: -16,
										left: -4,
										position: "absolute",
									}}
								>
									{intl.formatMessage({ id: "COASTGUARD_CURRENT_SESSION" })}
								</Box>

								<Box
									sx={{
										fontSize: 16,
										fontWeight: 700,
										overflow: "hidden",
										whiteSpace: "nowrap",
										textOverflow: "ellipsis",
									}}
									title={userDetails.applicationName}
								>
									{userDetails.applicationName}
								</Box>

								<Box
									sx={{
										color: (theme) => theme.palette.text.secondary,
										fontSize: 14,
										overflow: "hidden",
										whiteSpace: "nowrap",
										textOverflow: "ellipsis",
									}}
									title={userDetails.multitenancyTemplateName ?? undefined}
								>
									{userDetails.multitenancyTemplateName}
								</Box>

								<Box
									sx={{
										color: (theme) => theme.palette.text.secondary,
										fontSize: 14,
										overflow: "hidden",
										whiteSpace: "nowrap",
										textOverflow: "ellipsis",
									}}
									title={userDetails.applicationTenantName}
								>
									{userDetails.applicationTenantName}
								</Box>
							</Box>
						</Box>

						<Box sx={{ display: "flex", mt: 2, justifyContent: "space-between" }}>
							<Button icon={<AccountCircleIcon fontSize="small" />} onClick={goToProfile} size="small">
								{intl.formatMessage({ id: "COASTGUARD_PROFILE" })}
							</Button>

							<Button icon={<LogoutIcon fontSize="small" />} onClick={doLogout} size="small">
								{intl.formatMessage({ id: "COASTGUARD_LOGOUT" })}
							</Button>
						</Box>
					</Box>

					<Box sx={{ position: "absolute", top: 8, right: 8 }}>
						<Tooltip title={intl.formatMessage({ id: "COASTGUARD_GO_TO_COASTGUARD" })} placement="bottom">
							<span>
								<Button
									icon={<LaunchIcon />}
									onClick={goToCoastguard}
									variant="text"
									size="small"
								/>
							</span>
						</Tooltip>
					</Box>

					<Divider />

					{hasMultipleSessions && (
						<Box
							sx={{
								mt: 2,
								pl: 2,
								pr: 2,
								flex: "1 1 auto",
								minHeight: 0,
								display: "flex",
								flexDirection: "column",
							}}
						>
							<Box sx={{ mb: 1 }}>{intl.formatMessage({ id: "COASTGUARD_ALL_MY_SESSIONS" })}</Box>
							<CoastguardSessionSelect
								sessions={authenticationGroupDetails.sessions}
								currentSessionUuid={userDetails.applicationSessionUuid}
								switchSession={doSwitchSession}
								logoutSession={doEndSession}
								hideSingleAccountDetails
							/>
						</Box>
					)}

					<Box sx={{ display: "flex", mt: 2, mb: 2, pl: 2, pr: 2, justifyContent: "space-between" }}>
						<Button
							variant="outlined"
							size="small"
							onClick={() => {
								getAuthorizeApplicationRedirect().then((res) => {
									window.location.href = res;
								});
								onPopoverClose();
							}}
						>
							{intl.formatMessage({ id: "COASTGUARD_ADD_ANOTHER_SESSION" })}
						</Button>

						{hasMultipleSessions && (
							<Button
								variant="outlined"
								size="small"
								onClick={async () => {
									await logoutAuthenticationGroupSessions();
									onPopoverClose();
								}}
							>
								{intl.formatMessage({ id: "COASTGUARD_END_ALL_SESSIONS" })}
							</Button>
						)}
					</Box>
				</Box>
			</Popover>
		</>
	);
};

export default CoastguardProfileToolbarItem;
